import {
  CAREGIVER_CITIZEN_STATUS_OPTIONS,
  CAREGIVER_PRIMARY_LANGUAGE_OPTIONS,
} from '../../shared/nexusPicklists';

export type CaregiverFormKind = 'application' | 'onboarding';

export interface CaregiverSelectOption {
  value: string;
  label: string;
}

export interface CaregiverFieldConfig {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'date' | 'select' | 'multiselect' | 'radio' | 'checkbox' | 'textarea' | 'file' | 'signature' | 'summary' | 'heading';
  required?: boolean;
  disabled?: boolean;
  options?: string[];
  selectOptions?: readonly CaregiverSelectOption[];
  helperText?: string;
  width?: 'full' | 'half' | 'third' | 'quarter';
}

export type CaregiverCardIcon =
  | 'accountBalance'
  | 'assignment'
  | 'calendar'
  | 'contact'
  | 'description'
  | 'favorite'
  | 'folder'
  | 'health'
  | 'home'
  | 'payment'
  | 'person'
  | 'signature'
  | 'work';

export interface CaregiverCardConfig {
  title: string;
  icon?: CaregiverCardIcon;
  description?: string;
  fields: CaregiverFieldConfig[];
}

export interface CaregiverSectionConfig {
  status: string;
  title: string;
  description?: string;
  cards: CaregiverCardConfig[];
}

const YES_NO = ['Yes', 'No'];
const YES_NO_I_DISAGREE = ['Yes', 'No', 'I disagree'];
const RELATIONSHIPS = [
  'Aunt',
  'Boyfriend',
  'Brother',
  'Brother-in-law',
  'Cousin',
  'Daughter',
  'Daughter-in-law',
  'Father',
  'Father-in-law',
  'Friend',
  'Girlfriend',
  'Granddaughter',
  'Grandfather',
  'Grandmother',
  'Grandson',
  'Mother',
  'Mother-in-law',
  'Neighbor',
  'Nephew',
  'Niece',
  'Nurse',
  'Partner',
  'Sister',
  'Sister-in-law',
  'Social Worker',
  'Son',
  'Son-in-law',
  'Spouse',
  'Uncle',
  'Other',
];
const DAY_AVAILABILITY = ['Morning', 'Afternoon', 'Night', 'Live-In'];
const COUNTRY_OF_BIRTH = [
  'United States of America',
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cape Verde',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo',
  'Congo (Dem. Rep.)',
  "Cote d'Ivoire (Ivory Coast)",
  'Costa Rica',
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kosovo',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macao',
  'Macedonia',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar [Burma]',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Korea',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestinian Territories',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russian Federation',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'St. Vincent and the Grenadines',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City',
  'Venezuela',
  'Viet Nam',
  'Yemen',
  'Zambia',
  'Zimbabwe',
];
const GENDER_RESTRICTION_OPTIONS = ['Male Only', 'Female Only', 'No Restriction'];
const AREA_OF_WORK = [
  'Bronx',
  'Brooklyn',
  'Manhattan',
  'Nassau County',
  'Queens',
  'Rockland County',
  'Staten Island',
  'Westchester County',
  'Yonkers',
];
const ALLERGIES_AFRAID_OF = ['Dogs', 'Cats', 'Smoke', 'Other', 'None of the above'];
const SKILLS = [
  'Hoyer Lift',
  'Kosher Friendly',
  'Bed Bound',
  'Colostomy',
  'Dementia',
  'Foley Catheter',
  'Hospice',
  'Oxygen',
  'Wheelchair Bound',
  'None of the above',
];
const YEARS_EXPERIENCE = [
  'New HHA - No Experience',
  'Less than a year',
  '1 year',
  '2 years',
  '3 years',
  '4 years',
  '5 years',
  '6 years',
  '7 years',
  '8 years',
  '9 years',
  '10+ years',
];
const FEDERAL_FILING_STATUS = ['Married', 'Single or Married filing separately', 'Head of household'];
const NY_FILING_STATUS = ['Married', 'Single or Married filing separately', 'Head of household'];
const BANK_ACCOUNT_TYPE = ['Checking', 'Savings'];
const PAYMENT_METHODS = ['Direct Deposit', 'Payment Card'];
const BENEFIT_WAIVER = ['Yes', 'No'];
const FLU_DECLINATION_REASONS = [
  'FEAR - I believe I will get influenza if I get the vaccine',
  'FEAR - I do not like needles',
  'RELIGION - My philosophical and/or religious beliefs prohibit vaccination',
  'ALLERGIC - I have an allergy or medical contraindication to receiving the vaccine',
  'OTHER - Please specify',
];
const TB_ASSESSMENT_STATUS = ['PENDING', 'REVIEWED', 'NEEDS_FOLLOW_UP'];

const applicationAvailabilityFields: CaregiverFieldConfig[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
].flatMap((day) => {
  const label = `${day[0].toUpperCase()}${day.slice(1)}`;
  return [
    {
      name: `${day}Availability`,
      label,
      type: 'multiselect' as const,
      options: DAY_AVAILABILITY,
    },
  ];
});

export const APPLICATION_SECTIONS: CaregiverSectionConfig[] = [
  {
    status: 'New',
    title: 'New',
    cards: [
      {
        title: 'Basic Info',
        icon: 'person',
        fields: [
          { name: 'firstName', label: 'First Name', required: true },
          { name: 'lastName', label: 'Last Name', required: true },
          { name: 'phone', label: 'Phone', type: 'tel', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true },
          { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true },
          {
            name: 'primaryLanguage',
            label: 'Primary Language',
            type: 'select',
            selectOptions: CAREGIVER_PRIMARY_LANGUAGE_OPTIONS,
            required: true,
          },
        ],
      },
      {
        title: 'Address',
        icon: 'home',
        fields: [
          {
            name: 'street',
            label: 'Street Address',
            helperText: 'Include your house number and street name.',
            required: true,
          },
          { name: 'aptSuite', label: 'Apt / Suite' },
          { name: 'city', label: 'City', required: true },
          { name: 'state', label: 'State', required: true },
          { name: 'zipCode', label: 'Zip Code', required: true },
        ],
      },
      {
        title: 'Emergency Contact',
        icon: 'contact',
        fields: [
          { name: 'emergencyContactName', label: 'Name', required: true, width: 'full' },
          { name: 'emergencyContactRelationship', label: 'Relationship', type: 'select', options: RELATIONSHIPS, required: true },
          { name: 'emergencyContactPhone', label: 'Phone', type: 'tel', required: true },
        ],
      },
    ],
  },
  {
    status: 'Employment',
    title: 'Employment',
    cards: [
      {
        title: 'Personal Details',
        icon: 'person',
        fields: [
          { name: 'eyeColor', label: 'Eye Color', required: true },
          { name: 'height', label: 'Height', required: true },
          { name: 'hairColor', label: 'Hair Color', required: true },
          { name: 'weight', label: 'Weight', required: true },
          { name: 'countryOfBirth', label: 'Country of Birth', type: 'select', options: COUNTRY_OF_BIRTH, required: true },
        ],
      },
      {
        title: 'Employment',
        icon: 'work',
        fields: [
          { name: 'authorizedToWorkInUs', label: 'Are you authorized for emloyment in the U.S.?', type: 'select', options: YES_NO, required: true },
          { name: 'skills', label: 'Do you have experience working with any of the following?', type: 'multiselect', options: SKILLS, required: true },
          { name: 'previouslyWorkedAsHha', label: 'I have previously worked as an HHA', type: 'checkbox' },
          { name: 'yearsExperienceAsHha', label: 'Years experience as HHA', type: 'select', options: YEARS_EXPERIENCE, required: true },
          { name: 'hasFelony', label: 'Have you been convicted of a crime in New York State or any other jurisdiction?', type: 'select', options: YES_NO, required: true },
          { name: 'doAbuse', label: 'Do you have a final finding of patient or resident abuse?', type: 'select', options: YES_NO, required: true },
        ],
      },
    ],
  },
  {
    status: 'Availability',
    title: 'Availability',
    cards: [
      {
        title: 'Work Preferences',
        icon: 'work',
        fields: [
          { name: 'areaOfWork', label: 'Area of Work', type: 'multiselect', options: AREA_OF_WORK, required: true },
        ],
      },
      {
        title: 'Patient & Environment Preferences',
        icon: 'health',
        fields: [
          { name: 'ableToDrive', label: 'Do you drive?', type: 'select', options: YES_NO, required: true },
          { name: 'genderRestriction', label: 'Are you able to work with both female & male patients?', type: 'select', options: GENDER_RESTRICTION_OPTIONS, required: true },
          { name: 'allergyFearfulOfHeading', label: 'Are you allergic/fearful of:', type: 'heading', width: 'full' },
          { name: 'allergyCat', label: 'Cats', type: 'radio', options: YES_NO, required: true },
          { name: 'allergyDog', label: 'Dogs', type: 'radio', options: YES_NO, required: true },
          { name: 'allergyOtherPets', label: 'Other pets', type: 'radio', options: YES_NO, required: true },
          { name: 'allergySmoking', label: 'Are you allergic/sensitive to smoking?', type: 'radio', options: YES_NO, required: true },
        ],
      },
      {
        title: 'Availability',
        icon: 'calendar',
        description: 'Morning (before 12pm) | Afternoon (12pm-6pm) | Night (after 6pm) | Live-In (24hr Shift)',
        fields: applicationAvailabilityFields,
      },
    ],
  },
  {
    status: 'References',
    title: 'References',
    cards: [
      {
        title: 'Reference 1',
        icon: 'person',
        fields: [
          { name: 'reference1Name', label: 'Name', required: true, width: 'full' },
          { name: 'reference1Relationship', label: 'Relationship', required: true },
          { name: 'reference1Phone', label: 'Phone', type: 'tel', required: true },
        ],
      },
      {
        title: 'Reference 2',
        icon: 'person',
        fields: [
          { name: 'reference2Name', label: 'Name', required: true, width: 'full' },
          { name: 'reference2Relationship', label: 'Relationship', required: true },
          { name: 'reference2Phone', label: 'Phone', type: 'tel', required: true },
        ],
      },
    ],
  },
  {
    status: 'App Consent',
    title: 'App Consent',
    cards: [
      {
        title: 'Citizen Status',
        icon: 'person',
        description: 'I attest, under penalty of perjury, that I am (select one of the following):',
        fields: [
          {
            name: 'citizenStatus',
            label: 'Citizen Status',
            type: 'select',
            selectOptions: CAREGIVER_CITIZEN_STATUS_OPTIONS,
            required: true,
            width: 'half',
          },
        ],
      },
      {
        title: 'Review Forms',
        icon: 'description',
        description: "Click on the links below to review the NYS Department of Health's CHRC Consent and Link Homecare's Affidavit. You will need to upload a photo of your government issued ID and social security card. Once done, please sign and sumbit your application. If you have any questions, please feel free to contact hhahr@linkhomecare.com",
        fields: [],
      },
      {
        title: 'Signature',
        icon: 'signature',
        description: 'Electronic Signature Agreement: By signing this Acknowledgement, I agree that my electronic signature is the legally binding equivalent to my handwritten signature. Whenever I execute an electronic signature, it has the same validity and meaning as my handwritten signature. I will not, at any time in the future, repudiate the meaning of my electronic signature or claim that my electronic signature is not legally binding.',
        fields: [
          { name: 'applicantInitials', label: 'Applicant Initials', required: true, width: 'quarter' },
          { name: 'applicationSignature', label: 'Signature', type: 'signature', required: true, width: 'full' },
        ],
      },
    ],
  },
  {
    status: 'App Submitted',
    title: 'App Submitted',
    cards: [
      {
        title: 'Thank You for Submitting!',
        icon: 'description',
        description: 'Below is a summary of your application:',
        fields: [
          { name: 'applicationSummary', label: 'Download PDF', type: 'summary', width: 'full' },
        ],
      },
    ],
  },
];

export const ONBOARDING_SECTIONS: CaregiverSectionConfig[] = [
  {
    status: 'Onboarding',
    title: 'Onboarding',
    cards: [
      {
        title: 'Basic Info',
        icon: 'person',
        fields: [
          { name: 'firstName', label: 'First Name', required: true, disabled: true },
          { name: 'lastName', label: 'Last Name', required: true, disabled: true },
          { name: 'phone', label: 'Phone', type: 'tel', required: true },
          { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true },
          { name: 'ssn', label: 'SSN', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true },
        ],
      },
      {
        title: 'Address',
        icon: 'home',
        fields: [
          {
            name: 'street',
            label: 'Street Address',
            helperText: 'Include your house number and street name.',
            required: true,
          },
          { name: 'aptSuite', label: 'Apt / Suite' },
          { name: 'city', label: 'City', required: true },
          { name: 'state', label: 'State', required: true },
          { name: 'zipCode', label: 'Zip Code', required: true },
        ],
      },
    ],
  },
  {
    status: 'Contacts',
    title: 'Contacts',
    cards: [
      {
        title: 'Emergency Contact',
        icon: 'contact',
        fields: [
          { name: 'emergencyContactName', label: 'Name', required: true, width: 'full' },
          { name: 'emergencyContactRelationship', label: 'Relationship', type: 'select', options: RELATIONSHIPS, required: true },
          { name: 'emergencyContactPhone', label: 'Phone', type: 'tel', required: true },
        ],
      },
      {
        title: 'Emergency Contact 2',
        icon: 'contact',
        fields: [
          { name: 'emergencyContact2Name', label: 'Name', width: 'full' },
          { name: 'emergencyContact2Relationship', label: 'Relationship', type: 'select', options: RELATIONSHIPS },
          { name: 'emergencyContact2Phone', label: 'Phone', type: 'tel' },
        ],
      },
    ],
  },
  {
    status: 'Tax Forms',
    title: 'Tax Forms',
    cards: [
      {
        title: 'Federal Withholding',
        icon: 'accountBalance',
        fields: [
          { name: 'federalFilingStatus', label: 'Federal Filing Status', type: 'select', options: FEDERAL_FILING_STATUS, required: true },
          { name: 'dependentsUnder17', label: 'Dependents Under 17' },
          { name: 'otherDependents', label: 'Other Dependents' },
          { name: 'dependentCredit', label: 'Dependent Credit Amount' },
          { name: 'fedDeductions', label: 'Fed Deductions' },
          { name: 'fedAdditionalWithholdingAmount', label: 'Fed Additional Withholding Amount' },
          { name: 'multipleJobs', label: 'Multiple Jobs', type: 'checkbox' },
          { name: 'otherIncome', label: 'Other Income' },
        ],
      },
      {
        title: 'NY State Withholding',
        icon: 'accountBalance',
        fields: [
          { name: 'nyFilingStatus', label: 'NY Filing Status', type: 'select', options: NY_FILING_STATUS, required: true },
          { name: 'residentOfNewYorkCity', label: 'Are you a resident of New York City (this includes the Bronx, Brooklyn, Manhattan, Queens, and Staten Island)?', type: 'select', options: YES_NO, required: true },
          { name: 'residentOfYonkers', label: 'Are you a resident of Yonkers?', type: 'select', options: YES_NO, required: true },
          { name: 'nysAllowances', label: 'NYS Allowances', required: true },
          { name: 'nysAdditionalWithholdingAmount', label: 'NYS Additional Withholding Amount' },
          { name: 'nycAllowances', label: 'NYC Allowances', required: true },
          { name: 'nycAdditionalWithholdingAmount', label: 'NYC Additional Withholding Amount' },
        ],
      },
    ],
  },
  {
    status: 'WOTC',
    title: 'WOTC Questions',
    cards: [
      {
        title: 'Work Opportunity Credit: Form 8850',
        icon: 'description',
        description: 'Click here to view a sample of the WOTC 8850 form',
        fields: [
          { name: 'wotcQuestion1', label: 'Have you received a conditional certification from the state workforce agency (SWA) or a participating local agency for the work opportunity credit?', type: 'checkbox', width: 'full' },
          { name: 'wotcQuestion2', label: 'Select Yes, if any of the following statements apply to you. Otherwise select No.', type: 'checkbox', width: 'full' },
          { name: 'wotcQuestion3', label: 'Are you a veteran and you were unemployed for a period or periods totaling at least 6 months during the past year?', type: 'checkbox', width: 'full' },
          { name: 'wotcQuestion4', label: 'Are you a veteran entitled to compensation for a service-connected disability and you were discharged or released from active duty in the U.S. Armed Forces during the past year. released from active duty in the U.S. Armed Forces during the past year?', type: 'checkbox', width: 'full' },
          { name: 'wotcQuestion5', label: 'Check yes here if you are a veteran entitled to compensation for a service-connected disability and you were unemployed for a period or periods totalling at least 6 months during the past year.', type: 'checkbox', width: 'full' },
          { name: 'wotcQuestion6', label: 'Check yes here if you are a member of a family that:', type: 'checkbox', width: 'full' },
          { name: 'wotcQuestion7', label: 'Check yes here if you are in a period of unemployment that is at least 27 consecutive weeks and for all or part of that period you received unemployment compensation.', type: 'checkbox', width: 'full' },
        ],
      },
    ],
  },
  {
    status: 'Youth Credit',
    title: 'Youth Credit',
    cards: [
      {
        title: 'Youth Credit',
        icon: 'description',
        description: 'New York Youth Works Program - Youth Application',
        fields: [
          { name: 'youthQuestion1', label: 'Check yes here if you have a high school diploma, a GED or HSE diploma, satisfactorily completed a TASC exam, or enrolled in a TASC Program.', type: 'checkbox', width: 'full' },
          { name: 'youthQuestion2', label: 'I was unemployed prior to being hired or I do not have enough paid work.', type: 'checkbox', width: 'full' },
          { name: 'youthQuestion3', label: 'I confirm that I currently meet one or more of the youth categories listed below:', type: 'checkbox', width: 'full' },
        ],
      },
    ],
  },
  {
    status: 'Preferences',
    title: 'Preferences',
    cards: [
      {
        title: 'Payment Method',
        icon: 'payment',
        fields: [
          { name: 'paymentMethod', label: 'Payment Method', type: 'select', options: PAYMENT_METHODS, required: true },
          { name: 'financialInstitution', label: 'Financial Institution', required: true },
          { name: 'bankAccountNumber', label: 'Bank Account Number', required: true },
          { name: 'abaRoutingNumber', label: 'ABA Routing Number', required: true },
          { name: 'bankAccountReEntry', label: 'Confirm Bank Account Number', required: true },
          { name: 'bankAccountType', label: 'Bank Account Type', type: 'select', options: BANK_ACCOUNT_TYPE, required: true },
        ],
      },
      {
        title: 'Patient & Environment Preferences',
        icon: 'health',
        fields: [
          { name: 'ableToDrive', label: 'Do you drive?', type: 'select', options: YES_NO, required: true },
          { name: 'genderRestriction', label: 'Are you able to work with both female & male patients?', type: 'select', options: GENDER_RESTRICTION_OPTIONS, required: true },
          { name: 'allergyFearfulOfHeading', label: 'Are you allergic/fearful of:', type: 'heading', width: 'full' },
          { name: 'allergyCat', label: 'Cats', type: 'radio', options: YES_NO, required: true },
          { name: 'allergyDog', label: 'Dogs', type: 'radio', options: YES_NO, required: true },
          { name: 'allergyOtherPets', label: 'Other pets', type: 'radio', options: YES_NO, required: true },
          { name: 'allergySmoking', label: 'Are you allergic/sensitive to smoking?', type: 'radio', options: YES_NO, required: true },
        ],
      },
      {
        title: 'Availability',
        icon: 'calendar',
        description: 'Morning (before 12pm) | Afternoon (12pm-6pm) | Night (after 6pm) | Live-In (24hr Shift)',
        fields: applicationAvailabilityFields,
      },
    ],
  },
  {
    status: 'TB Assessment',
    title: 'TB Assessment',
    cards: [
      {
        title: 'TB Assessment',
        icon: 'health',
        description: 'Click to view the TB Assessment document',
        fields: [
          ...Array.from({ length: 5 }, (_, index) => ({
            name: `tbRiskQuestion${index + 1}`,
            label: `TB Risk Question ${index + 1}`,
            type: 'radio' as const,
            options: YES_NO,
            required: true,
            width: 'full' as const,
          })),
        ],
      },
      {
        title: 'TB Symptoms',
        icon: 'health',
        fields: [
          ...Array.from({ length: 9 }, (_, index) => ({
            name: `tbSymptomsQuestion${index + 1}`,
            label: `TB Symptoms Question ${index + 1}`,
            type: 'radio' as const,
            options: YES_NO,
            required: true,
            width: 'full' as const,
          })),
          { name: 'tbAssessmentStatus', label: 'TB Assessment Status', type: 'select' as const, options: TB_ASSESSMENT_STATUS },
          { name: 'tbAssessmentReviewDate', label: 'TB Assessment Review Date', type: 'date' as const },
        ],
      },
    ],
  },
  {
    status: 'I-9',
    title: 'I-9',
    cards: [
      {
        title: 'I-9 Verification',
        icon: 'contact',
        description: 'Every new hire is required by law to verify both identity and eligibility to work in the U.S. To do this, you will need to provide documents that support your employment eligibility. The I-9 verification process will also require your signature.',
        fields: [],
      },
      {
        title: 'Flu Declination',
        icon: 'favorite',
        fields: [
          { name: 'fluDeclinationReason', label: 'Flu Declination Reason', type: 'multiselect', options: FLU_DECLINATION_REASONS },
          { name: 'fluDeclineOtherReason', label: 'Flu Declination Other Reason' },
        ],
      },
      {
        title: 'Medical Benefits',
        icon: 'health',
        fields: [
          { name: 'waivedBenefits', label: 'I would like to waive medical benefits', type: 'select', options: BENEFIT_WAIVER, required: true, width: 'half' },
        ],
      },
      {
        title: 'Review Documents',
        icon: 'description',
        description: 'Please click on the link to review each of the documents listed below. At the end of the onboarding process you will be prompted to sign, wherein you agree as the person identified, to all of the conditions listed in the documents.',
        fields: [],
      },
      {
        title: 'Signature',
        icon: 'signature',
        description: 'Electronic Signature Agreement: By signing this Acknowledgement, I agree that my electronic signature is the legally binding equivalent to my handwritten signature. Whenever I execute an electronic signature, it has the same validity and meaning as my handwritten signature. I will not, at any time in the future, repudiate the meaning of my electronic signature or claim that my electronic signature is not legally binding.',
        fields: [
          { name: 'applicantInitials', label: 'Applicant Initials', required: true },
          { name: 'onboardingSignature', label: 'Signature', type: 'signature', required: true, width: 'full' },
        ],
      },
    ],
  },
  {
    status: 'Review',
    title: 'Review',
    cards: [],
  },
];

export function getSections(kind: CaregiverFormKind, includeYouthCredit = true) {
  if (kind === 'application') return APPLICATION_SECTIONS;
  return includeYouthCredit
    ? ONBOARDING_SECTIONS
    : ONBOARDING_SECTIONS.filter((section) => section.status !== 'Youth Credit');
}
